import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import api from '../../utils/api';
import { toast } from 'sonner';
import { HardDrive, CheckCircle2, XCircle, Activity, ExternalLink, Loader2 } from 'lucide-react';

const AssetsTab = ({ clientId }) => {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAssets();
  }, [clientId]);

  const fetchAssets = async () => {
    try {
      const response = await api.get('/agents');
      // Filter assets by client_id
      const clientAssets = response.data.filter(agent => agent.client_id === clientId);
      setAssets(clientAssets);
    } catch (error) {
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'online') return <CheckCircle2 className="w-5 h-5 text-green-600" />;
    if (status === 'offline') return <XCircle className="w-5 h-5 text-red-600" />;
    return <Activity className="w-5 h-5 text-yellow-600" />;
  };

  const getStatusBadge = (status) => {
    if (status === 'online') return <Badge className="bg-green-100 text-green-700 border-green-200">Online</Badge>;
    if (status === 'offline') return <Badge className="bg-red-100 text-red-700 border-red-200">Offline</Badge>;
    return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Pending</Badge>;
  };

  const formatLastSeen = (lastSeen) => {
    if (!lastSeen) return 'Never';
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-20 text-center">
          <Loader2 className="w-16 h-16 mx-auto mb-4 text-blue-600 animate-spin" />
          <p className="text-slate-500">Loading assets...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <HardDrive className="w-5 h-5 text-blue-600" />
          <span>Client Assets</span>
        </CardTitle>
        <CardDescription>
          All devices and systems assigned to this client
        </CardDescription>
      </CardHeader>
      <CardContent>
        {assets.length === 0 ? (
          <div className="text-center py-12">
            <HardDrive className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No assets found</h3>
            <p className="text-slate-500">This client doesn't have any assets assigned yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Hostname</TableHead>
                  <TableHead>Friendly Name</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>OS</TableHead>
                  <TableHead>CPU</TableHead>
                  <TableHead>Memory</TableHead>
                  <TableHead>Last Seen</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assets.map((asset) => (
                  <TableRow key={asset.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(asset.status)}
                        {getStatusBadge(asset.status)}
                      </div>
                    </TableCell>
                    <TableCell className="font-semibold">
                      {asset.hostname}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {asset.friendly_name || '-'}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {asset.ip_address || 'Detecting...'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {asset.system_details?.os_name || 'N/A'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {asset.cpu_info?.split(' ').slice(0, 3).join(' ') || 'N/A'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {asset.total_memory_gb ? `${Math.round(asset.total_memory_gb)} GB` : 'N/A'}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {formatLastSeen(asset.last_seen)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        v{asset.version}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/assets/${asset.id}`)}
                        className="gap-2"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Summary Stats */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-800">{assets.length}</p>
                  <p className="text-sm text-slate-500">Total Assets</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {assets.filter(a => a.status === 'online').length}
                  </p>
                  <p className="text-sm text-slate-500">Online</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">
                    {assets.filter(a => a.status === 'offline').length}
                  </p>
                  <p className="text-sm text-slate-500">Offline</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AssetsTab;
